import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { api, resolveLogoUrl } from '@/lib/api/client'
import { useMe } from '@/features/auth/api'
import { date, inrFull } from '@/lib/format'
import { useLabel } from '@/lib/labels'

interface Receipt {
  receiptNumber: string
  source: 'FIELD' | 'DIGITAL'
  mode: string
  reference: string | null
  loanAccountNo: string | null
  customerName: string | null
  amount: number
  collectedAt: string
  status: string
  cancelledReason?: string | null
  collectedByEmpId: string | null
  collectedByName: string | null
  split: { emi: number; bounce: number; lpp: number; otherPenalties: number; otherFees: number } | null
  lenderName: string | null
  /** The tenant's Terms & Conditions; empty when none are set. */
  terms?: string[]
}

const HEADS: [keyof NonNullable<Receipt['split']>, string][] = [
  ['emi', 'EMI'],
  ['bounce', 'Bounce charge'],
  ['lpp', 'Late payment charge'],
  ['otherPenalties', 'Other penalties'],
  ['otherFees', 'Other fees'],
]

/**
 * The customer's receipt as the agent's app shows it — same fields, same
 * wording. The API limits a manager to receipts taken in their org subtree.
 */
export function ReceiptDrawer({ collectionId, onClose }: { collectionId: string; onClose: () => void }) {
  // The same logo the agent's printed receipt carries.
  const logo = resolveLogoUrl(useMe().data?.branding?.logoUrl)
  const label = useLabel()
  const { data: r, isPending, isError } = useQuery({
    queryKey: ['receipts', collectionId],
    queryFn: async () => (await api.get<Receipt>(`/v1/receipts/${collectionId}`)).data,
  })
  const provisional = r && r.status !== 'CONFIRMED'
  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Receipt">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 bg-navy-900/50 backdrop-blur-sm"
      />
      <aside className="flex h-full w-full max-w-[min(480px,95vw)] flex-col bg-navy-50 shadow-lift animate-slide-in-right">
        <div className="flex h-13 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5">
          <span className="text-2xs font-semibold uppercase tracking-wider text-navy-700">Receipt</span>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X className="h-3.5 w-3.5" />
            Close
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          {isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
          {isError && <p className="text-sm text-negative">This receipt could not be opened.</p>}
          {r && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="font-mono text-base">{r.receiptNumber}</CardTitle>
                  <Badge tone={r.status === 'CANCELLED' ? 'negative' : provisional ? 'warning' : 'positive'}>{r.status === 'CANCELLED' ? 'Cancelled' : provisional ? 'Provisional' : 'Confirmed'}</Badge>
                </div>
                {(logo || r.lenderName) && (
                  <div className="flex items-center gap-2">
                    {logo && <img src={logo} alt="" className="h-8 w-8 object-contain" />}
                    {r.lenderName && <p className="text-xs text-muted-foreground">{r.lenderName}</p>}
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-1.5 text-sm">
                <p className="text-2xl font-semibold tabular-nums text-navy-900">{inrFull(r.amount)}</p>
                {r.status === 'CANCELLED' && <div className="text-negative" role="status">
                  <p>Cancelled — cash not received by accounts</p><p>{r.cancelledReason}</p>
                </div>}
                <Row k="Customer" v={r.customerName ?? '—'} />
                <Row k="Loan account" v={r.loanAccountNo ?? '—'} />
                <Row k="Date" v={date(r.collectedAt)} />
                <Row k="Payment mode" v={label(r.source === 'DIGITAL' ? 'channel' : 'paymentMode', r.mode)} />
                {r.reference && <Row k="Reference" v={r.reference} />}
                {r.split && (
                  <>
                    <p className="pt-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Paid towards</p>
                    {HEADS.filter(([h]) => Number(r.split![h]) > 0).map(([h, name]) => (
                      <Row key={h} k={name} v={inrFull(r.split![h])} />
                    ))}
                  </>
                )}
                <Row k="Collected by" v={[r.collectedByName, r.collectedByEmpId].filter(Boolean).join(' · ') || '—'} />
                {r.terms && r.terms.length > 0 && (
                  <div className="border-t border-slate-200 pt-3">
                    <p className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Terms &amp; Conditions
                    </p>
                    <ol className="mt-1 list-decimal space-y-0.5 pl-4 text-2xs text-muted-foreground">
                      {r.terms.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ol>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </aside>
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-right text-navy-900">{v}</span>
    </div>
  )
}
